/**
 * mandate-registration.ts - the Brokermandat registration with the insurers (Stefan P5 V2.0 steps
 * 22-24, Kap. 7.10, 7.11, 7.13): the vocabulary, the one status word set for every process, the
 * exhaustive transition table, the exception rules of the Meldeliste and the wire DTOs.
 *
 * A registration IS a mandate_insurer_notification row with prozess 'p5_broker'. The analysis
 * notification (prozess 'p4_analyse') shares the row, the statuses and the words.
 *
 * Pure data and pure functions only, shared by api and web.
 */

import type { NotificationResolutionLevel } from './carrier-notification';
import type { MandateInsurerNotificationStatus } from './versicherer-benachrichtigung';

// ── vocabulary ──────────────────────────────────────────────────────────────────────────────

/** How a Meldung went out (the row's report_mode). */
export const REGISTRATION_REPORT_MODE = {
  AUTOMATISCH: 'automatisch',
  FREIGEGEBEN: 'freigegeben',
  AUTOMATISCH_UEBERTRAGEN: 'automatisch_uebertragen',
  MANUELL: 'manuell',
} as const;
export type RegistrationReportMode = (typeof REGISTRATION_REPORT_MODE)[keyof typeof REGISTRATION_REPORT_MODE];

export const REGISTRATION_REPORT_MODE_LABEL: Record<RegistrationReportMode, string> = {
  automatisch: 'automatisch',
  freigegeben: 'freigegeben',
  automatisch_uebertragen: 'automatisch übertragen',
  manuell: 'manuell',
};

/**
 * The codes stored on a row (exception_codes): the exception rules of the Meldeliste (Kap. 7.10), the
 * reasons a planned or claimed row went to a broker, and send_pending, the marker of a planned row
 * that waits for its job.
 */
export const REGISTRATION_EXCEPTION_CODE = {
  SEND_PENDING: 'send_pending',
  KEINE_ADRESSE: 'keine_adresse',
  HAUPTADRESSE: 'hauptadresse',
  VN_ABWEICHEND: 'vn_abweichend',
  POLICE_IN_ABKLAERUNG: 'police_in_abklaerung',
  POLICE_IN_ABKLAERUNG_TEILWEISE: 'police_in_abklaerung_teilweise',
  NUR_MANUELL: 'nur_manuell',
  KEIN_BROKERCODE: 'kein_brokercode',
  KEIN_ABSENDER: 'kein_absender',
  KEIN_VERSICHERER: 'kein_versicherer',
  KEIN_DOKUMENT: 'kein_dokument',
  VERSAND_UNKLAR: 'versand_unklar',
  VERSAND_FEHLGESCHLAGEN: 'versand_fehlgeschlagen',
  EFFECTIVE_DATE_ASSUMED: 'effective_date_assumed',
  /** Planned before the reminder loop's cutoff: waits for a broker instead of going out by itself. */
  ALTBESTAND: 'altbestand',
  /** Closed by the scan because its Brokermandat ended (terminated, cancelled). */
  MANDAT_BEENDET: 'mandat_beendet',
  /** Planned but never sent when its mandate's termination was approved (the insurer gets the Auflösung). */
  MANDAT_AUFGELOEST: 'mandat_aufgeloest',
  /** An Auflösungsmeldung closed by the Widerruf of its termination. */
  WIDERRUF: 'widerruf',
} as const;
export type RegistrationExceptionCode = (typeof REGISTRATION_EXCEPTION_CODE)[keyof typeof REGISTRATION_EXCEPTION_CODE];

/** The chip text of each code (the exception_labels of a row). */
export const REGISTRATION_EXCEPTION_LABEL: Record<RegistrationExceptionCode, string> = {
  send_pending: 'Versand ausstehend',
  keine_adresse: 'Keine Meldeadresse',
  hauptadresse: 'Hauptadresse verwendet',
  vn_abweichend: 'VN abweichend',
  police_in_abklaerung: 'Police in Abklärung',
  police_in_abklaerung_teilweise: 'Police in Abklärung nicht gemeldet',
  nur_manuell: 'Nur manuelle Meldung',
  kein_brokercode: 'Kein Brokercode',
  kein_absender: 'Kein Absender',
  kein_versicherer: 'Versicherer nicht erfasst',
  kein_dokument: 'Unterzeichnetes Mandat nicht lesbar',
  versand_unklar: 'Versand unklar',
  versand_fehlgeschlagen: 'Versand fehlgeschlagen',
  effective_date_assumed: 'Wirkungsdatum angenommen',
  altbestand: 'Altbestand',
  mandat_beendet: 'Mandat beendet',
  mandat_aufgeloest: 'Mandat aufgelöst',
  widerruf: 'Auflösung widerrufen',
};

/**
 * The codes of an earlier send attempt: a new claim strips them (the audit keeps their history). The
 * planning codes (keine_adresse, nur_manuell, ...) stay on the row as history.
 */
export const TRANSIENT_REGISTRATION_CODES: readonly RegistrationExceptionCode[] = [
  REGISTRATION_EXCEPTION_CODE.SEND_PENDING,
  REGISTRATION_EXCEPTION_CODE.VERSAND_FEHLGESCHLAGEN,
  REGISTRATION_EXCEPTION_CODE.VERSAND_UNKLAR,
  REGISTRATION_EXCEPTION_CODE.KEIN_ABSENDER,
  REGISTRATION_EXCEPTION_CODE.KEIN_VERSICHERER,
  REGISTRATION_EXCEPTION_CODE.KEIN_DOKUMENT,
  REGISTRATION_EXCEPTION_CODE.ALTBESTAND,
];

/** The tenant's Meldemodus (insurer_notification_frist_config.report_mode for p5_broker). */
export const REGISTRATION_SETTINGS_REPORT_MODE = {
  AUTOMATISCH: 'automatisch',
  VORSCHLAG_MIT_FREIGABE: 'vorschlag_mit_freigabe',
} as const;
export type RegistrationSettingsReportMode =
  (typeof REGISTRATION_SETTINGS_REPORT_MODE)[keyof typeof REGISTRATION_SETTINGS_REPORT_MODE];
export const REGISTRATION_SETTINGS_REPORT_MODE_VALUES: readonly RegistrationSettingsReportMode[] = [
  'automatisch',
  'vorschlag_mit_freigabe',
];
export const REGISTRATION_SETTINGS_REPORT_MODE_LABEL: Record<RegistrationSettingsReportMode, string> = {
  automatisch: 'Automatisch',
  vorschlag_mit_freigabe: 'Vorschlag mit Freigabe',
};

/** Why a broker forces a deferred Meldung out (POST .../jetzt-melden). */
export const REGISTRATION_JETZT_MELDEN_REASON_VALUES = ['police_geklaert', 'versicherer_verlangt', 'sonstiges'] as const;
export type RegistrationJetztMeldenReason = (typeof REGISTRATION_JETZT_MELDEN_REASON_VALUES)[number];

/**
 * After this many hourly sweeps the job is given up and a task «Meldung nicht ausgelöst» takes over;
 * the row keeps send_pending until a broker acts («Meldung auslösen» restarts the count).
 */
export const REGISTRATION_MAX_SWEEPS = 3;

/** Planned: written before any mail, no claim. */
export const PLANNED_REGISTRATION_STATUSES: readonly MandateInsurerNotificationStatus[] = ['zurueckgestellt', 'zur_freigabe'];

/** Live: one per (mandate, insurer) for a Brokermandat (uq_mandate_insurer_notification_p5_live). */
export const LIVE_REGISTRATION_STATUSES: readonly MandateInsurerNotificationStatus[] = [
  'sending',
  'zurueckgestellt',
  'zur_freigabe',
  'benachrichtigt',
  'erinnert',
  'klaerung',
  'rueckfrage',
];

/** Closed: superseded only by a new row («Erneut melden») or moved back by a Korrektur. */
export const CLOSED_REGISTRATION_STATUSES: readonly MandateInsurerNotificationStatus[] = [
  'bestaetigt',
  'abgelehnt',
  'manuell_geschlossen',
  'ausgetragen',
  'informiert',
];

/**
 * Every status and where a row may go from it. Every non-terminal status can be closed by a broker
 * with a Notiz; abgelehnt and manuell_geschlossen are superseded by a new row; bestaetigt only goes
 * back to klaerung (Korrektur). A Record over the union: a new status fails to compile without a row.
 */
export const LEGAL_REGISTRATION_TRANSITIONS: Record<MandateInsurerNotificationStatus, readonly MandateInsurerNotificationStatus[]> = {
  sending: ['benachrichtigt', 'zur_freigabe'],
  zurueckgestellt: ['sending', 'manuell_geschlossen'],
  zur_freigabe: ['sending', 'manuell_geschlossen'],
  // ausgetragen: a source row whose Auflösung was recorded; informiert: an information-only Auflösungsmeldung
  // at its record (a service rule keeps it to info_only rows).
  benachrichtigt: ['erinnert', 'klaerung', 'rueckfrage', 'bestaetigt', 'abgelehnt', 'manuell_geschlossen', 'ausgetragen', 'informiert'],
  erinnert: ['klaerung', 'rueckfrage', 'bestaetigt', 'abgelehnt', 'manuell_geschlossen', 'ausgetragen'],
  klaerung: ['bestaetigt', 'abgelehnt', 'rueckfrage', 'manuell_geschlossen', 'ausgetragen'],
  rueckfrage: ['benachrichtigt', 'klaerung', 'bestaetigt', 'abgelehnt', 'manuell_geschlossen', 'ausgetragen'],
  bestaetigt: ['klaerung', 'ausgetragen'],
  abgelehnt: [],
  manuell_geschlossen: [],
  // the Wiedereintragung after a Widerruf that followed a confirmed Austragung («Wiedereintragung
  // bestätigen» on the p5 source, with a Beleg; a service rule keeps it to p5_broker rows).
  ausgetragen: ['bestaetigt'],
  informiert: [],
};

/** The statuses a broker can close with a Notiz (every non-terminal one except a running claim). */
export const CLOSABLE_REGISTRATION_STATUSES: readonly MandateInsurerNotificationStatus[] = (
  Object.keys(LEGAL_REGISTRATION_TRANSITIONS) as MandateInsurerNotificationStatus[]
).filter((s) => LEGAL_REGISTRATION_TRANSITIONS[s].includes('manuell_geschlossen'));

const STATUS_WORD: Record<MandateInsurerNotificationStatus, string> = {
  sending: 'Wird gesendet',
  zurueckgestellt: 'Zurückgestellt',
  zur_freigabe: 'Zur Freigabe',
  benachrichtigt: 'Gemeldet',
  erinnert: 'Erinnert',
  klaerung: 'Klärung',
  rueckfrage: 'Rückfrage',
  bestaetigt: 'Eingetragen',
  abgelehnt: 'Abgelehnt',
  manuell_geschlossen: 'Geschlossen',
  ausgetragen: 'Ausgetragen',
  informiert: 'Informiert',
};

/** The words of an Auflösungsmeldung (prozess p6_aufloesung) where they differ from STATUS_WORD. */
const AUFLOESUNG_STATUS_WORD: Partial<Record<MandateInsurerNotificationStatus, string>> = {
  benachrichtigt: 'Auflösung gemeldet',
  erinnert: 'Auflösung erinnert',
  bestaetigt: 'Ausgetragen',
};

/** `YYYY-MM-DD` as `dd.mm.yyyy` (a date column, no time zone involved); null for another shape. */
export function formatDateDe(isoDate: string | null | undefined): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate ?? '');
  return m ? `${m[3]}.${m[2]}.${m[1]}` : null;
}

/**
 * The status of a registration row in Stefan's words, the same for the analysis notification and the
 * Brokermandat registration. Gemeldet carries «(automatisch übertragen)» for a Meldung to a new insurer
 * of a registered customer; Eingetragen carries «(manuell bestätigt)» and «ab <Wirkungsdatum>».
 */
export function registrationStatusLabel(row: {
  prozess?: string | null;
  status: MandateInsurerNotificationStatus;
  report_mode?: string | null;
  confirmation_mode?: string | null;
  effective_date?: string | null;
}): string {
  // An analysis row whose customer ended the mandate was informed, never de-registered.
  if (row.status === 'ausgetragen' && row.prozess === 'p4_analyse') return 'Aufgelöst (informiert)';
  const word = (row.prozess === 'p6_aufloesung' ? AUFLOESUNG_STATUS_WORD[row.status] : undefined) ?? STATUS_WORD[row.status] ?? row.status;
  if (row.status === 'benachrichtigt' && row.report_mode === 'automatisch_uebertragen') {
    return `${word} (automatisch übertragen)`;
  }
  if (row.status === 'bestaetigt') {
    const manual = row.confirmation_mode === 'manuell' ? ' (manuell bestätigt)' : '';
    const from = formatDateDe(row.effective_date);
    return `${word}${manual}${from ? ` ab ${from}` : ''}`;
  }
  return word;
}

/** The Fristen bands Stefan set for the Brokermandat (the table CHECK stays 1-60). */
export const P5_FRIST_BOUNDS = {
  frist_1: [5, 30],
  frist_2: [3, 20],
} as const;

// ── the exception rules of the Meldeliste (Kap. 7.10) ───────────────────────────────────────

/** What happens to one insurer of the Meldeliste. */
export type RegistrationOutcome = 'send' | 'zur_freigabe' | 'zurueckgestellt';

/** A policy whose number is the ingest placeholder for a missing one. */
export const POLICY_NUMBER_PLACEHOLDER_PREFIX = 'ENTWURF-';

/** A policy still «in Abklärung»: a draft, or one with a placeholder number. */
export function isPolicyInAbklaerung(policy: { status: string; policyNumber: string | null }): boolean {
  return policy.status === 'draft' || (policy.policyNumber ?? '').toUpperCase().startsWith(POLICY_NUMBER_PLACEHOLDER_PREFIX);
}

export interface RegistrationExceptionInput {
  /** The F1 resolution of the insurer's Meldeadresse for p5_broker. */
  resolutionLevel: NotificationResolutionLevel;
  /** The reasons the broker gave for the scope items of this insurer he excluded. */
  excludedReasons: readonly string[];
  /** The policies the Meldung would list. */
  policies: ReadonlyArray<{ id: string; status: string; policyNumber: string | null }>;
  /** How many declared insurances without a policy the Meldung would list. */
  relationshipCount: number;
  /** The insurer wants every Meldung released by a broker. */
  manualReportOnly: boolean;
  /** The Brokercode VU the insurer gave the broker, or null. */
  brokerVuCode: string | null;
}

export interface RegistrationExceptionResult {
  codes: RegistrationExceptionCode[];
  outcome: RegistrationOutcome;
  /** The policies the Meldung lists (those in Abklärung dropped when others remain). */
  policyIds: string[];
  /** The policies in Abklärung left out of the Meldung. */
  droppedPolicyIds: string[];
}

const OUTCOME_RANK: Record<RegistrationOutcome, number> = { send: 0, zur_freigabe: 1, zurueckgestellt: 2 };

/**
 * The exception rules for one insurer of the Meldeliste. Several rules combine; the strictest outcome
 * wins (zurueckgestellt over zur_freigabe over send). Takes no policy-holder input: the Meldeliste
 * lists the customer's own policies, so «VN abweichend» is only the reason a broker gave when he
 * excluded a scope item. Pure.
 */
export function evaluateRegistrationExceptions(input: RegistrationExceptionInput): RegistrationExceptionResult {
  const codes: RegistrationExceptionCode[] = [];
  let outcome: RegistrationOutcome = 'send';
  const raise = (code: RegistrationExceptionCode, to: RegistrationOutcome): void => {
    codes.push(code);
    if (OUTCOME_RANK[to] > OUTCOME_RANK[outcome]) outcome = to;
  };

  if (input.resolutionLevel === 'none') raise('keine_adresse', 'zur_freigabe');
  else if (input.resolutionLevel === 'carrier_default') raise('hauptadresse', 'send');

  if (input.excludedReasons.includes('vn_abweichend')) raise('vn_abweichend', 'zur_freigabe');

  const inAbklaerung = input.policies.filter((p) => isPolicyInAbklaerung(p));
  const ready = input.policies.filter((p) => !isPolicyInAbklaerung(p));
  let policyIds = input.policies.map((p) => p.id);
  let droppedPolicyIds: string[] = [];
  if (inAbklaerung.length > 0) {
    if (ready.length === 0 && input.relationshipCount === 0) {
      raise('police_in_abklaerung', 'zurueckgestellt');
    } else {
      policyIds = ready.map((p) => p.id);
      droppedPolicyIds = inAbklaerung.map((p) => p.id);
      raise('police_in_abklaerung_teilweise', 'send');
    }
  }

  if (input.manualReportOnly) raise('nur_manuell', 'zur_freigabe');
  if (input.brokerVuCode === null || input.brokerVuCode.trim() === '') raise('kein_brokercode', 'send');

  return { codes, outcome, policyIds, droppedPolicyIds };
}

/** One line of the Policenliste: «<Sparte> · Police Nr. <Nr>», or «· ohne Policennummer». Pure. */
export function policenlisteLine(line: { sparte: string | null; policyNumber: string | null }): string {
  const sparte = line.sparte !== null && line.sparte.trim() !== '' ? line.sparte : 'Versicherung';
  return line.policyNumber !== null && line.policyNumber.trim() !== ''
    ? `${sparte} · Police Nr. ${line.policyNumber}`
    : `${sparte} · ohne Policennummer`;
}

/** The Policenliste of a mail: one «- » line each, or a placeholder line. Pure. */
export function formatPolicenliste(lines: ReadonlyArray<{ sparte: string | null; policyNumber: string | null }>): string {
  return lines.length > 0 ? lines.map((l) => `- ${policenlisteLine(l)}`).join('\n') : '- (keine Policen angegeben)';
}

/** «Name, Adresse, UID CHE-...»: the parts that are known, nothing empty. Pure. */
export function kundeAdresseUidLine(parts: { name: string; addressLine: string | null; uid: string | null }): string {
  return [parts.name, parts.addressLine, parts.uid !== null && parts.uid !== '' ? `UID ${parts.uid}` : null]
    .filter((p): p is string => p !== null && p !== '')
    .join(', ');
}

// ── wire DTOs ───────────────────────────────────────────────────────────────────────────────

/** GET /mandates/:id/registrations row (snake_case like the analysis rows; no notes). */
export interface RegistrationDto {
  id: string;
  mandate_id: string;
  prozess: string;
  carrier_uid: string;
  /** The normalised UID (upper case, alphanumerics only): one insurer whatever the spelling. */
  carrier_key: string;
  carrier_name: string | null;
  versicherer_id: string | null;
  relationship_ids: string[];
  policy_ids: string[];
  recipient_email: string | null;
  resolution_level: NotificationResolutionLevel;
  template_ref: string;
  /** The attached document; null on an Auflösungsmeldung (no attachment). */
  document_id: string | null;
  communication_id: string | null;
  /** The mail of a send whose record failed (versand_unklar): it may already have reached the insurer. */
  unclear_communication_id: string | null;
  vorgangs_ref: string;
  sent_at: string | null;
  sent_by_app_user_id: string | null;
  sender_app_user_id: string | null;
  response_due_at: string | null;
  frist_arbeitstage: number;
  status: MandateInsurerNotificationStatus;
  status_label: string;
  status_changed_at: string | null;
  report_mode: RegistrationReportMode | null;
  report_mode_label: string | null;
  exception_codes: RegistrationExceptionCode[];
  exception_labels: string[];
  broker_vu_code: string | null;
  requested_effective_date: string | null;
  released_at: string | null;
  released_by_app_user_id: string | null;
  effective_date: string | null;
  confirmation_mode: string | null;
  reminded_at: string | null;
  /** Reminders sent in the current round (a restarted Frist starts at 0). */
  reminded_count: number;
  /** The deadline the last reminder set (Frist 2); the live one is response_due_at. */
  frist_2_due_at: string | null;
  /** The escalation task of the row (Klärung), when one is open or was opened this round. */
  escalation_task_id: string | null;
  /** Why the last reminder did not go out (no_sender, mailbox_send_failed); null when it did. */
  reminder_error_code: RegistrationReminderErrorCode | null;
  /** The reminder mail (an insurer's reply threading onto it matches the row). */
  reminder_communication_id: string | null;
  /** The reminders the row's prozess allows (the tenant's config; 1 for a Brokermandat). */
  erinnerungen: number;
  closed_at: string | null;
  sweep_count: number;
  overdue: boolean;
  /** The listed policies (policy_ids), with what the Meldung named them by. */
  policies: Array<{ policy_id: string; policy_number: string | null; line_of_business: string | null }>;
  /** The listed declared insurances (relationship_ids). */
  relationships: Array<{ relationship_id: string; sparte: string | null; policy_number: string | null }>;
  /**
   * On a Freigabe's answer only: the mail of the «Versand unklar» row was found in Brokly's record or
   * the mailbox and recorded, nothing was sent again.
   */
  already_sent?: boolean;
  /** Which decisions the row's status allows (the caller's permissions are checked on top). */
  actions: {
    freigeben: boolean;
    jetzt_melden: boolean;
    schliessen: boolean;
    erneut_melden: boolean;
    /** The insurer's answer, recorded by a broker (the transition table, mandate.eingangskontrolle). */
    bestaetigen: boolean;
    ablehnen: boolean;
    rueckfrage: boolean;
    rueckfrage_beantwortet: boolean;
    korrektur: boolean;
    /** «Jetzt erinnern»: the reminder now, from the caller's mailbox (a gemeldet row not yet reminded). */
    erinnern: boolean;
    /** «Eskalieren»: the Klärung task now (a gemeldet or erinnert row without one). */
    eskalieren: boolean;
    /** «Versand bestätigt» / «Nicht versendet» on a reminder whose send is unclear. */
    versand_bestaetigen: boolean;
    nicht_versendet: boolean;
  };
}

/** POST /mandates/:id/registrations/run */
export interface RunRegistrationsResultDto {
  mandate_id: string;
  /** True when the send job was enqueued. */
  enqueued: boolean;
  /** Planned rows waiting for the job (send_pending). */
  pending: number;
}

/** POST /mandates/:id/registrations/:regId/jetzt-melden */
export interface JetztMeldenRegistrationBody {
  reasonCode: RegistrationJetztMeldenReason;
}

/** POST /mandates/:id/registrations/erneut-melden */
export interface ErneutMeldenRegistrationBody {
  carrierUid: string;
}

/** POST /mandates/:id/registrations/:regId/schliessen */
export interface SchliessenRegistrationBody {
  notiz: string;
}

/** GET /admin/einstellungen/brokermandat-meldung */
export interface BrokerRegistrationSettingsDto {
  report_mode: RegistrationSettingsReportMode;
  escalation_funktionsgruppe_id: string | null;
  scope_auto_confirm: boolean;
  frist_1: number;
  frist_2: number;
  ist_default: boolean;
}

/** PUT /admin/einstellungen/brokermandat-meldung */
export interface SetBrokerRegistrationSettingsBody {
  report_mode: RegistrationSettingsReportMode;
  escalation_funktionsgruppe_id?: string | null;
  scope_auto_confirm: boolean;
}

/** PATCH /carriers/:carrierUid/registration-settings */
export interface CarrierRegistrationSettingsBody {
  manual_report_only: boolean;
}
export interface CarrierRegistrationSettingsDto {
  carrier_uid: string;
  manual_report_only: boolean;
}

/** The activity events of the registration (Kap. 12; activity rows with meta.event). */
export const MANDATE_REGISTRATION_EVENTS = {
  SENT: 'mandate_registration.sent',
  EXCEPTION: 'mandate_registration.exception',
  REMINDED: 'mandate_registration.reminded',
  ESCALATED: 'mandate_registration.escalated',
  /** A Meldung to a new insurer of a registered customer went out (Kap. 12, step 30). */
  AUTO_TRANSFERRED: 'mandate_registration.auto_transferred',
  /** An Auflösungsmeldung went out (a mandate termination). */
  TERMINATION_SENT: 'mandate_registration.termination_sent',
  /** The insurer confirmed the Austragung (or was informed). */
  DEREGISTERED: 'mandate_registration.deregistered',
} as const;

// ── the reminder and the escalation of a registration without an answer (Kap. 7.10, 7.12) ──────

/** Why a reminder did not go out (the row is counted as reminded either way: exactly one attempt). */
export const REGISTRATION_REMINDER_ERROR_CODE = {
  NO_SENDER: 'no_sender',
  MAILBOX_SEND_FAILED: 'mailbox_send_failed',
  NO_RECIPIENT: 'keine_adresse',
  /** The claim of a reminder whose mail is on its way; cleared by the record. */
  PENDING: 'pending',
  /** The mail may have gone out but was never recorded (a crash, a failed record): a human checks. */
  VERSAND_UNKLAR: 'versand_unklar',
} as const;
export type RegistrationReminderErrorCode = (typeof REGISTRATION_REMINDER_ERROR_CODE)[keyof typeof REGISTRATION_REMINDER_ERROR_CODE];
export const REGISTRATION_REMINDER_ERROR_LABEL: Record<RegistrationReminderErrorCode, string> = {
  no_sender: 'Erinnerung nicht versendet (kein Postfach)',
  mailbox_send_failed: 'Erinnerung nicht versendet (Versand fehlgeschlagen)',
  keine_adresse: 'Erinnerung nicht versendet (keine Adresse)',
  pending: 'Erinnerung wird versendet',
  versand_unklar: 'Erinnerung: Versand unklar, bitte prüfen',
};

/**
 * A reminder that did not go out may be sent again by a broker («Jetzt erinnern»): still the one
 * reminder. A «Versand unklar» one may have gone out: a broker first says which («Versand bestätigt» /
 * «Nicht versendet»), never a blind second mail.
 */
export const RETRYABLE_REMINDER_ERROR_CODES: readonly RegistrationReminderErrorCode[] = [
  'no_sender',
  'mailbox_send_failed',
  'keine_adresse',
];

/** The tasks of the reminder loop (process mandate_registration; meta.kind is the stable key). */
export const REGISTRATION_REMINDER_TASK = {
  /** Step 27 for a Brokermandat (Stefan 7.1). */
  ESKALATION_BROKER: { kind: 'eintragung_nicht_bestaetigt', title: 'Eintragung Brokermandat nicht bestätigt' },
  /** P4 step 24 for an Analysemandat. */
  ESKALATION_ANALYSE: { kind: 'versicherer_ohne_rueckmeldung', title: 'Versicherer ohne Rückmeldung' },
  FEHLGESCHLAGEN: { kind: 'erinnerung_fehlgeschlagen', title: 'Erinnerung an Versicherer fehlgeschlagen' },
  MANUELL_SENDEN: { kind: 'erinnerung_manuell_senden', title: 'Erinnerung an Versicherer manuell senden' },
  VERSAND_UNKLAR: { kind: 'erinnerung_versand_unklar', title: 'Erinnerung an Versicherer: Versand prüfen' },
  /** P6 step 17: the insurer did not confirm the Austragung. */
  ESKALATION_AUFLOESUNG: { kind: 'austragung_nicht_bestaetigt', title: 'Austragung nicht bestätigt' },
  /** An insurer refuses to de-register the broker (a data problem: there are never two brokers). */
  AUSTRAGUNG_VERWEIGERT: { kind: 'austragung_verweigert', title: 'Versicherer verweigert Austragung' },
} as const;

/** The one-time task of a tenant's Altbestand: registrations and Brokermandate older than the cutoff. */
export const REGISTRATION_ALTBESTAND_TASK = { kind: 'altbestand_pruefen', title: 'Altbestand prüfen' } as const;

/** The tasks a reminder that went out after all settles (the failure tasks of its row). */
export const REGISTRATION_REMINDER_FAILURE_TASK_KINDS: readonly string[] = [
  'erinnerung_fehlgeschlagen',
  'erinnerung_manuell_senden',
  'erinnerung_versand_unklar',
];

/** The task kinds whose detail shows the registration they are about. */
export const REGISTRATION_REMINDER_TASK_KINDS: readonly string[] = Object.values(REGISTRATION_REMINDER_TASK).map((t) => t.kind);

/** Working days the escalation task is due in (Stefan 7.10). */
export const REGISTRATION_ESCALATION_DUE_ARBEITSTAGE = 5;

/**
 * Which hand on the reminder loop a row allows (the caller's keys are checked on top): «Jetzt erinnern»
 * for a gemeldet row whose prozess has a reminder left, or for a row whose reminder did not go out
 * (still the one reminder); «Eskalieren» for a gemeldet or erinnert row without a Klärung task. Neither
 * once the mandate ended. Pure.
 */
export function registrationReminderActions(row: {
  status: MandateInsurerNotificationStatus;
  reminded_count: number;
  erinnerungen: number;
  escalation_task_id: string | null;
  reminder_error_code: string | null;
  mandate_open: boolean;
}): { erinnern: boolean; eskalieren: boolean; versand_bestaetigen: boolean; nicht_versendet: boolean } {
  const unclear = row.reminder_error_code === 'versand_unklar';
  const awaiting = row.status === 'benachrichtigt' || row.status === 'erinnert';
  if (!row.mandate_open || !awaiting) return { erinnern: false, eskalieren: false, versand_bestaetigen: unclear, nicht_versendet: unclear };
  const retry = row.reminder_error_code !== null && (RETRYABLE_REMINDER_ERROR_CODES as readonly string[]).includes(row.reminder_error_code);
  const first = row.status === 'benachrichtigt' && row.reminded_count < row.erinnerungen && row.reminder_error_code === null;
  return {
    erinnern: row.erinnerungen > 0 && (first || retry),
    eskalieren: row.escalation_task_id === null,
    versand_bestaetigen: unclear,
    nicht_versendet: unclear,
  };
}

/** GET /tasks/:taskId/altbestand: what a task «Altbestand prüfen» is about (one mandate). */
export interface RegistrationAltbestandDto {
  task_id: string;
  mandate_id: string;
  mandate_kind: 'analyse' | 'broker';
  /** The mandate's rows the task lists, as the registration panel shows them (either prozess). */
  registrations: RegistrationDto[];
  /** Scope insurers of a Brokermandat still without any Meldung («Meldung auslösen» reports them). */
  unreported_carriers: Array<{ carrier_uid: string | null; carrier_name: string | null }>;
}
